import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { $ } from '../lib/api';
import { fmtMonthDayTime } from '../lib/format';
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  Pill, Btn, PillTone,
} from '../components/ui';
import { toast } from '../lib/toast';
import { useAuthStore } from '../store/auth';

type Tab = 'all' | 'approval' | 'new_lead' | 'read';
const TAB_PARAM: Record<Tab, any> = {
  all: {},
  approval: { tab: 'approval' },
  new_lead: { tab: 'new_lead' },
  read: { tab: 'read' },
};

// 优先级用专属色：紧急/重要 → 红，避免和合同状态的橙色 pill 视觉混淆
const PRIO_TONE: Record<string, PillTone> = {
  urgent: 'danger', high: 'danger', normal: 'primary', low: 'default',
};
const PRIO_LABEL: Record<string, string> = {
  urgent: '紧急', high: '重要', normal: '普通', low: '低',
};

/** Friendly display for the channels JSON array. Pure CSS chips. */
function ChannelChips({ channels }: { channels?: string[] | null }) {
  if (!channels || channels.length === 0) return <span className="time-text muted">站内</span>;
  const map: Record<string, string> = {
    web: '站内', app: '应用', dingtalk: '钉钉', sms: '短信', email: '邮件',
  };
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {channels.map((c) => (
        <span key={c} className="check-pip" style={{ background: 'var(--bg)', color: 'var(--text-2)' }}>
          {map[c] ?? c}
        </span>
      ))}
    </div>
  );
}

export function NotificationsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const role = useAuthStore((s) => s.user?.role);
  const canSeeNewLead = role === 'manager' || role === 'admin'; // 新线索待分配 tab 仅 manager+admin（销售看不到）

  const params = useMemo(() => ({ page, pageSize, ...TAB_PARAM[tab] }), [tab, page]);
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => $.get<{ items: any[]; total: number }>('/notifications', params),
  });
  // 分类 tab 各条数（单接口聚合，避免多次 count）
  const { data: counts } = useQuery({
    queryKey: ['notifications-tab-counts'],
    queryFn: () => $.get<{ all: number; read: number; approval: number; newLead: number }>('/notifications/tab-counts'),
  });

  // 进入「审批/新线索」tab 自动把该类未读标已读（读完自动进已读）
  const markTabRead = useMutation({
    mutationFn: (t: string) => $.post('/notifications/mark-read-by-type', { tab: t }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-tab-counts'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      qc.invalidateQueries({ queryKey: ['notifications-feed'] });
    },
  });
  useEffect(() => {
    if (tab === 'approval' || tab === 'new_lead') markTabRead.mutate(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // 一键清除已读（软删 dismissedAt 留痕、不真删）：当前 tab 限定（approval/new_lead 只清该类；all/read 清全部已读）
  const clearRead = useMutation({
    mutationFn: () => $.post('/notifications/clear-read', { tab }),
    onSuccess: () => {
      toast.success('已清除已读');
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-tab-counts'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      qc.invalidateQueries({ queryKey: ['notifications-feed'] });
    },
  });

  // batch-fix#H22：逐条标已读(点击行) + 全部已读——原通知中心点击只 nav 不标已读、无"标已读"入口、铃铛清不到 0。
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['notifications-tab-counts'] });
    qc.invalidateQueries({ queryKey: ['notifications-unread'] });
    qc.invalidateQueries({ queryKey: ['notifications-feed'] });
  };
  const markOne = useMutation({
    mutationFn: (id: number) => $.post('/notifications/mark-read', { ids: [id] }),
    onSettled: invalidateAll,
  });
  const markAll = useMutation({
    mutationFn: () => $.post('/notifications/mark-all-read', {}),
    onSuccess: () => { toast.success('全部已读'); invalidateAll(); },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader
        breadcrumb={['资源', '通知中心']}
        title="通知中心"
        subtitle="系统/团队/客户的实时消息"
        actions={
          <>
            <Btn kind="default" onClick={() => markAll.mutate()}>
              {markAll.isPending ? '处理中…' : '全部已读'}
            </Btn>
            <Btn kind="default" onClick={() => clearRead.mutate()}>
              {clearRead.isPending ? '处理中…' : '一键清除已读'}
            </Btn>
          </>
        }
      />
      <div className="work-content">
        <StatsRow>
          <StatCard label="审批待处理" value={counts?.approval ?? 0} unit="条" valueColor="warning" />
          {canSeeNewLead && <StatCard label="新线索待分配" value={counts?.newLead ?? 0} unit="条" valueColor="warning" />}
          <StatCard label="已读" value={counts?.read ?? 0} unit="条" />
          <StatCard label="总计" value={counts?.all ?? 0} unit="条" />
        </StatsRow>

        <FilterBar
          active={tab}
          onChange={(k) => { setTab(k as Tab); setPage(1); }}
          tabs={[
            { key: 'approval', label: '审批通知', count: counts?.approval },
            ...(canSeeNewLead ? [{ key: 'new_lead', label: '新线索待分配', count: counts?.newLead }] : []),
            { key: 'read', label: '已读', count: counts?.read },
            { key: 'all', label: '全部', count: counts?.all },
          ]}
        />
        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>标题</th>
                <th style={{ width: '34%' }}>内容</th>
                <th style={{ width: '8%' }}>优先级</th>
                <th style={{ width: '14%' }}>推送渠道</th>
                <th style={{ width: '10%' }}>状态</th>
                <th style={{ width: '14%' }}>时间</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无通知</td></tr>
              )}
              {items.map((n: any) => {
                const isUnread = !n.readAt;
                return (
                  <tr key={n.id} className={isUnread ? 'notif-unread' : 'notif-read'} onClick={() => { if (!n.readAt) markOne.mutate(n.id); if (n.linkUrl) nav(n.linkUrl); }}>
                    <td>
                      <div className="cust-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {isUnread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)', flexShrink: 0 }} />}
                        {n.title}
                      </div>
                    </td>
                    <td><span className="time-text" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.content}</span></td>
                    <td><Pill tone={PRIO_TONE[n.priority] ?? 'default'}>{PRIO_LABEL[n.priority] ?? n.priority}</Pill></td>
                    <td><ChannelChips channels={n.channels} /></td>
                    <td>
                      {isUnread ? <Pill tone="primary">未读</Pill> : <Pill tone="default">已读</Pill>}
                    </td>
                    <td>
                      <div className="time-text">{fmtMonthDayTime(n.createdAt)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 0 && <TableFooter total={total} page={page} pageSize={pageSize} onPage={setPage} />}
        </TableWrap>
      </div>
    </>
  );
}
